// Wall-clock time in a named zone, and back again.
//
// ICD-S05 §7.8: a rule written in wall-clock time — "claims process Wednesday
// at 03:00", "lineups lock at 1:00 pm" — is resolved through the league's IANA
// zone AT THE INSTANT IN QUESTION, and no offset is ever stored. An offset
// captured in March is wrong in November, and the way that surfaces is a
// waiver run that silently moves an hour twice a year and a lock that moves
// with it.
//
// `Intl` is the only timezone database available on workerd, and it converts
// one way: instant in, local parts out. Everything here is that one direction
// plus an inverse built by measuring.
//
// THE TWO HARD DAYS, and what this file does about each:
//
//   SPRING FORWARD. 02:30 does not exist; the clock jumps 02:00 → 03:00. A
//   rule naming 02:30 resolves PAST the gap rather than before it, landing at
//   03:30 local — exactly twenty-four hours after the day before, so the rule
//   fires once and never early.
//
//   FALL BACK. 01:30 happens twice. We take THE EARLIER of the two, so a rule
//   naming it fires once. Taking the later one would also fire once; taking
//   "whichever is next" would fire twice, which is the bug — a waiver run
//   processing the same claims again an hour later.
//
// Both are conformance checks (20), and both are the reason this is a file
// with tests rather than three lines inline.

/** The zone's local calendar and clock at an instant. */
export interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  /** 0 = Sunday, matching `LeagueSettings.waivers.processing.weekday`. */
  weekday: number;
}

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function formatter(zone: string): Intl.DateTimeFormat {
  const cached = FORMATTERS.get(zone);
  if (cached) return cached;
  // `hour12: false` rather than `hourCycle`, and the `% 24` below, because ICU
  // versions disagree about whether midnight formats as 00 or 24 under it.
  const made = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  FORMATTERS.set(zone, made);
  return made;
}

/** Is this a zone this runtime knows? Checked at settings validation, not at tick time. */
export function isKnownZone(zone: string): boolean {
  try {
    formatter(zone).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}

/** The zone's local calendar and clock at `at`. */
export function partsIn(zone: string, at: Date): LocalParts {
  const parts = formatter(zone).formatToParts(at);
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((p) => p.type === type);
    return found ? Number(found.value) : 0;
  };
  const year = read('year');
  const month = read('month');
  const day = read('day');
  const hour = read('hour') % 24;
  const minute = read('minute');
  return {
    year,
    month,
    day,
    hour,
    minute,
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
}

/** How far ahead of UTC the zone is at `at`, in milliseconds. Negative west of Greenwich. */
export function offsetAt(zone: string, at: Date): number {
  const p = partsIn(zone, at);
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, 0);
  // The seconds and milliseconds of `at` are not in `asIfUtc`, so round `at`
  // down to the minute before subtracting or every offset is off by them.
  const atMinute = Math.floor(at.getTime() / 60_000) * 60_000;
  return asIfUtc - atMinute;
}

/**
 * The instant at which the zone's wall clock reads these local parts.
 *
 * Measure, then check the answer. The offset in force at the naive instant
 * gives one candidate and the offset in force at THAT gives another; when they
 * agree there is no transition nearby and the first is right. When they
 * disagree, one of the two hard days is in range and the only sound test is to
 * render each candidate back and see which one actually reads as what was
 * asked for:
 *
 *   BOTH READ CORRECTLY — the fall-back repeat. Take the EARLIER, so a weekly
 *   rule in the repeated hour fires once rather than twice.
 *
 *   NEITHER READS CORRECTLY — the spring-forward gap; the local time asked for
 *   never happens. Take the LATER, which is the same elapsed distance from
 *   yesterday's occurrence as any ordinary day — 02:30 on the gap day lands at
 *   03:30 local, exactly twenty-four hours after 02:30 the day before. Landing
 *   before the gap instead would fire the rule EARLY, and a waiver run that
 *   processes claims before its stated time is worse than one that is late.
 *
 * A two-pass with no check lands before the gap, which is what this did until
 * `zone.test.ts` watched it answer 01:30.
 */
export function instantOfLocal(
  zone: string,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): Date {
  const naive = Date.UTC(year, month - 1, day, hour, minute, 0);
  const first = naive - offsetAt(zone, new Date(naive));
  const second = naive - offsetAt(zone, new Date(first));
  if (first === second) return new Date(first);

  const reads = (at: number): boolean => {
    const p = partsIn(zone, new Date(at));
    return (
      p.year === year &&
      p.month === month &&
      p.day === day &&
      p.hour === hour &&
      p.minute === minute
    );
  };
  const firstReads = reads(first);
  const secondReads = reads(second);
  if (firstReads && secondReads) return new Date(Math.min(first, second));
  if (firstReads) return new Date(first);
  if (secondReads) return new Date(second);
  return new Date(Math.max(first, second));
}

/** `HH:MM` → `[hour, minute]`. The shape `ClockTime` already enforces. */
export function splitClockTime(time: string): [number, number] {
  const [h, m] = time.split(':');
  return [Number(h), Number(m)];
}

/**
 * The first instant strictly after `from` at which the zone's clock reads
 * `time`, on `weekday` when one is given.
 *
 * Strictly after, so calling it with the instant a run just happened returns
 * the next one rather than the same one forever.
 */
export function nextWallClock(
  zone: string,
  from: Date,
  time: string,
  weekday: number | null,
): Date | null {
  const [hour, minute] = splitClockTime(time);
  const here = partsIn(zone, from);
  // Eight days covers a weekly rule from any starting point, plus the day a
  // transition can push a candidate across.
  for (let ahead = 0; ahead <= 8; ahead++) {
    const day = new Date(Date.UTC(here.year, here.month - 1, here.day + ahead));
    if (weekday !== null && day.getUTCDay() !== weekday) continue;
    const at = instantOfLocal(
      zone,
      day.getUTCFullYear(),
      day.getUTCMonth() + 1,
      day.getUTCDate(),
      hour,
      minute,
    );
    if (at.getTime() > from.getTime()) return at;
  }
  return null;
}

/**
 * Every instant in `(after, until]` at which the zone's clock reads `time` on
 * `weekday`. What the sweep asks to catch up on a range it slept through.
 */
export function wallClocksBetween(
  zone: string,
  after: Date,
  until: Date,
  time: string,
  weekday: number | null,
): Date[] {
  const found: Date[] = [];
  let cursor = after;
  // A daily rule over a long outage is still bounded: 400 covers more than a
  // year, and anything past that is a database restore rather than a sweep.
  for (let i = 0; i < 400; i++) {
    const next = nextWallClock(zone, cursor, time, weekday);
    if (!next || next.getTime() > until.getTime()) break;
    found.push(next);
    cursor = next;
  }
  return found;
}

/** The instant of `time` on the same local day as `on`. Used by a fixed-time lock. */
export function wallClockOnDayOf(zone: string, on: Date, time: string): Date {
  const [hour, minute] = splitClockTime(time);
  const p = partsIn(zone, on);
  return instantOfLocal(zone, p.year, p.month, p.day, hour, minute);
}
